import { Check, Copy, Search } from "lucide-react";
import { useEffect, useMemo, type FC } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from "@/components/ui/input-group";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useClientSearch, type ClientOption } from "../hooks/useClientSearch";
import type { SelectedClient } from "../types";
import { clientInitials } from "../utils";

type Props = {
  value: SelectedClient | null;
  onChange: (client: SelectedClient | null) => void;
  query: string;
  onQueryChange: (query: string) => void;
  onSwitchToNew: () => void;
  /** The firm's client count, once known. */
  onTotalChange?: (total: number | null) => void;
  className?: string;
};

const toSelected = ({ id, name, email }: ClientOption): SelectedClient => ({
  id,
  name,
  email
});

const DuplicateFlag: FC<{ client: ClientOption }> = ({ client }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        data-testid="possible-duplicate"
        className="flex shrink-0 items-center gap-1 text-sm text-warn"
      >
        <Copy className="size-3" aria-hidden />
        Possible Duplicate
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs">
      Possible duplicate of{" "}
      {client.possibleDuplicates
        .map((d) => `${d.name} (${d.reason.toLowerCase()})`)
        .join(", ")}
      .
    </TooltipContent>
  </Tooltip>
);

/** The client picker: a search field over the firm's clients, newest first. */
export const ExistingClientStep: FC<Props> = ({
  value,
  onChange,
  query,
  onQueryChange,
  onSwitchToNew,
  onTotalChange,
  className
}) => {
  const { clients, total, isLoading, error } = useClientSearch(query);

  useEffect(() => {
    onTotalChange?.(total);
  }, [onTotalChange, total]);

  // Keep the chosen client visible even when the current search hides it.
  const rows = useMemo<ClientOption[]>(
    () =>
      value && !clients.some((c) => c.id === value.id)
        ? [{ ...value, possibleDuplicates: [] }, ...clients]
        : clients,
    [clients, value]
  );

  return (
    <div
      className={cn(
        "flex max-w-160 flex-col rounded-lg bg-elevated ring-1 ring-line-2",
        className
      )}
    >
      <div className="p-2">
        <InputGroup className="rounded-md has-[[data-slot=input-group-control]:focus-visible]:ring-0">
          <InputGroupAddon>
            <Search className="size-3.5 text-fg-3" />
          </InputGroupAddon>
          <InputGroupInput
            className="text-base text-fg placeholder:text-fg-4"
            aria-label="Search clients"
            placeholder="Search clients..."
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
          />
        </InputGroup>
      </div>

      <div className="max-h-56 overflow-y-auto px-1 pb-1">
        {isLoading && rows.length === 0 ? (
          <div className="flex flex-col gap-1 px-1 pb-1">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : error ? (
          <p className="px-3 py-6 text-center text-sm text-danger">{error}</p>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
            <p className="text-base text-fg-2">
              {query.trim()
                ? `No clients match "${query.trim()}".`
                : "You don't have any clients yet."}
            </p>
            <Button variant="ghost" size="sm" onClick={onSwitchToNew}>
              Enter a New Client Instead
            </Button>
          </div>
        ) : (
          <div role="listbox" aria-label="Clients" className="flex flex-col">
            {rows.map((client) => {
              const selected = value?.id === client.id;
              return (
                <button
                  key={client.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => onChange(selected ? null : toSelected(client))}
                  className={cn(
                    "flex h-9 w-full min-w-0 items-center gap-2.5 rounded-md px-2.5 text-left outline-none transition-colors duration-100 focus-visible:ring-2 focus-visible:ring-primary/40",
                    selected ? "bg-fill-2" : "hover:bg-fill-1"
                  )}
                >
                  <Avatar size="xs">
                    <AvatarFallback>{clientInitials(client)}</AvatarFallback>
                  </Avatar>
                  <span className="max-w-1/2 shrink-0 truncate text-base text-fg">
                    {client.name || client.email}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
                    {client.name ? client.email : null}
                  </span>
                  {client.possibleDuplicates.length ? (
                    <DuplicateFlag client={client} />
                  ) : null}
                  {selected ? (
                    <Check
                      className="size-3.5 shrink-0 text-accent-seed"
                      aria-hidden
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
